import { DISABLE_SOLD_OUT_MESSAGING_TAG, ONLINE_SALES_PRICE_TAG, SHOP_LOCALE } from './constants';
import { transform } from 'lodash-es';
import { getCookie } from './cookies';

/**
 * Checks if the user-agent (normalized) string contains any known mobile user-agents
 * !!!: This function is copied into the shared-snippet-across-layouts.liquid file so that we can have browser
 * load-timing there.
 **/
export const IS_NATIVE_APP_CLIENT = (() => {
  const cleanUserAgent = navigator.userAgent.toLowerCase();
  return ['followapp', 'aloandroid', 'aloios'].some((part) => cleanUserAgent.includes(part));
})();

export const isSiteInEnglish = () => SHOP_LOCALE === 'en';

// Version is in the format MAJOR.MINOR.PATCH, .PATCH may be left out
export const NATIVE_APP_OS_AND_VERSION = ((): ['android' | 'ios' | 'unknown', string] => {
  if (!IS_NATIVE_APP_CLIENT) return ['unknown', '0.0'];
  const cleanUserAgent = navigator.userAgent.toLowerCase();
  const matches = cleanUserAgent.match(/(aloios|aloandroid)\/([0-9.]+)/i);
  if (!matches || !matches[2]) return ['unknown', '0.0'];
  //@ts-expect-error
  return [matches[1]?.replace('alo', '') || 'unknown', matches[2]];
})();

const MINIMUM_APP_VERSION = {
  android: [2, 5],
  ios: [2, 7, 1],
  unknown: null,
};
export const isValidAppVersion = () => {
  const [os, appVersion] = NATIVE_APP_OS_AND_VERSION;
  const appVersionArr = appVersion.split('.');
  const minVersionArr = MINIMUM_APP_VERSION[os];
  if (!minVersionArr) return false;
  for (let i = 0; i < minVersionArr.length; i += 1) {
    const appNumber = parseInt(appVersionArr[i] || '0', 10);
    const minNumber = minVersionArr[i] || 0;
    if (appNumber > minNumber) return true;
    if (appNumber < minNumber) return false;
    // if equal, move on to next set
  }
  return true; // if app version === minimum version
};

export const encodeURIComponentFormWWW = (aString: string) => {
  return encodeURIComponent(aString).replace(/%20/g, '+');
};

// Helper function
export const tryCatchWrapper = async (cb: (() => Promise<unknown | void>) | (() => void | unknown)) => {
  try {
    await cb();
  } catch (err) {
    console.error(err);
  }
};

export function handleize(string: string) {
  const a = 'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;';
  const b = 'aaaaaaaaaacccddeeeeeeeegghiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------';
  const p = new RegExp(a.split('').join('|'), 'g');

  return (
    string
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(p, (c) => b.charAt(a.indexOf(c))) // Replace special characters
      .replace(/&/g, '-and-') // Replace & with 'and'

      // ? [REVISIT] `no-useless-escape`
      // eslint-disable-next-line no-useless-escape
      .replace(/[^\w\-]+/g, '') // Remove all non-word characters
      // eslint-disable-next-line no-useless-escape
      .replace(/\-\-+/g, '-')
  ); // Replace multiple - with single -
}

// https://stackoverflow.com/questions/7394748/whats-the-right-way-to-decode-a-string-that-has-special-html-entities-in-it
export function decodeHtml(html: string) {
  var txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

export const pluralize = (count: number, str: string, suffix = 's') => `${count} ${str}${count !== 1 ? suffix : ''}`;

export const getUrlWithoutParams = (url: string | null) => url?.split('?')[0] || '';

/**
 * Tiny wrapper for `heap.track`.
 * Failures are silently ignored.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const heapTrack = (name: string, properties: Record<string, any>) => {
  try {
    //@ts-ignore
    //if(window.heap) ?
    window.heap.track(name, properties);
  } catch (err) {
    console.log('heap track did not track', err);
  }
};

// ? [REVISIT] what is going on here? we can't pass custom properties to NR?
export const reportErrorToNewRelic = (error: Error) => {
  // @ts-expect-error
  const newRelicThing = window.NREUM;
  if (window.Shopify.theme?.role !== 'main') {
    console.debug('Not sending to New Relic because not in production');
  } else if (typeof newRelicThing.noticeError !== 'function') {
    console.debug('Ignoring New Relic because New Relic not present');
  } else {
    newRelicThing.noticeError(error);
  }
};

export const recordCustomEventToNewRelic = (eventName: string, options: Record<string, unknown>) => {
  // @ts-expect-error
  const newRelicInstance = window.newrelic;
  try {
    if (newRelicInstance) {
      newRelicInstance.recordCustomEvent(eventName, options);
    } else {
      console.log('Failed to record custom event to NR: ', { newRelicInstance });
    }
  } catch (error) {
    console.log('Failed to record custom event to NR: ', { newRelicInstance });
  }
};

export const isInternetExplorer = (() => {
  const ua = window.navigator.userAgent.toLowerCase();
  if (ua.indexOf('windows nt') === -1) return false;
  return null !== ua.match(/(trident\/|msie )/i);
})();

/**
 * Simple retry logic for any promise
 */
export const retryPromise = async <T>(
  promiseFunc: (attemptNumber: number) => Promise<T>,
  numberOfTries: number = 1,
  { delay }: { delay?: number } = {}
): Promise<T> => {
  try {
    return await promiseFunc(numberOfTries - 1);
  } catch (err) {
    if (numberOfTries <= 1) throw err;
    if (delay) await sleep(delay);
    return await retryPromise(promiseFunc, numberOfTries - 1);
  }
};

export const poll = (fn: () => {}, timeout = 2000, interval = 100) => {
  const endTime = Number(new Date()) + timeout;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const checkCondition = function (resolve: (value: unknown) => void, reject: (reason?: any) => void) {
    const result = fn();
    if (result) {
      resolve(result);
    } else if (Number(new Date()) < endTime) {
      setTimeout(checkCondition, interval, resolve, reject);
    } else {
      reject(new Error('timed out for: ' + fn));
    }
  };

  return new Promise(checkCondition);
};

export const waitFor = (time: number) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      resolve(true);
    }, time);
  });
};

export const sleep = (ms = 0) =>
  new Promise((r) => {
    if (ms > 0) setTimeout(r, ms);
    else r(undefined);
  });

export const tellNativeAppWeHaveLoaded = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const webkit: any = window.webkit;
  try {
    if (webkit && webkit.messageHandlers && webkit.messageHandlers.pageDidLoad) {
      webkit.messageHandlers.pageDidLoad.postMessage({ status: 'success' });
    }
    //@ts-expect-error
    if (window && window.AloAndroid && 'function' === typeof window.AloAndroid.sendMessage) {
      //@ts-expect-error
      window.AloAndroid.sendMessage('pageDidLoad');
    }
  } catch (e: unknown) {
    reportErrorToNewRelic(e as Error);
    console.error('Unexpected error reporting page load status to Native Apps');
  }
};

// shouldn't be here
export const isBrowserCompliantCurrencyCode = (currencyCode: string) => {
  try {
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    });
    return true;
  } catch (err) {
    console.error(`'${currencyCode}' is not a browser compliant currency code`);
    // ? value in returning the err message?
    // return err as Error;
    return false;
  }
};

export const IS_iOS = (() => {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
})();

export function safeDecodeURIComponent(encodedURI: string, { consoleError = false } = {}): string {
  try {
    return decodeURIComponent(encodedURI);
  } catch (error) {
    if (consoleError) console.error(`Error decoding URI component: ${error}`);
    return encodedURI;
  }
}

const reportHeightToNativeWrapper = (heightInPixels: number) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const webkit: any = window.webkit;
  if (webkit && webkit.messageHandlers && webkit.messageHandlers.contentHeightDidChange) {
    webkit.messageHandlers.contentHeightDidChange.postMessage({ height: heightInPixels });
  }
};

export const resizeObserver = new ResizeObserver((entries) => {
  for (let entry of entries) {
    if (entry.contentBoxSize) {
      // Firefox implements `contentBoxSize` as a single content rect, rather than an array
      const contentBoxSize = Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize;
      const height = contentBoxSize.blockSize;
      reportHeightToNativeWrapper(height);
    } else {
      const height = entry.contentRect.height;
      reportHeightToNativeWrapper(height);
    }
  }
});

export const formatDateWithLocaleOptions = (
  dateString: string,
  options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }
) => {
  const date = new Date(dateString);
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  const offsetDate = new Date(date.getTime() + userTimezoneOffset);
  return offsetDate.toLocaleDateString(navigator.language, options);
};

export const convertDateToISOFormat = (date: Date) => {
  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
  const offsetDate = new Date(date.getTime() - userTimezoneOffset);
  const isoDate = offsetDate.toISOString().split('T')[0]; // remove the timezone piece
  return isoDate;
};

export const isTagPresent = (tags: string[], targetTag: string) =>
  tags.map((tag) => tag.toLowerCase()).includes(targetTag.toLowerCase());

export const checkIfCustomer = () => (window.getCustomerInfo ? window?.getCustomerInfo().isCustomer : false);

export const getAloApiAuthHeaders = () => {
  const wishlistShopifySettings = window.getWishlistSettings ? window.getWishlistSettings() : null;
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Shopify-Customer-ID': wishlistShopifySettings?.customerId.toString() || '',
    'X-Shopify-Timestamp': wishlistShopifySettings?.timestamp || '',
    'X-Shopify-Signature': wishlistShopifySettings?.signature || '',
    'X-Cookie-Domain': window.location.hostname.replace('www.', ''),
    'Cache-Control': 'max-age=0, no-cache, no-store, must-revalidate',
  };
};

export const getPScontext = () => {
  const wishlistShopifySettings = window.getWishlistSettings ? window.getWishlistSettings() : null;
  return {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Shopify-Customer-ID': wishlistShopifySettings?.customerId.toString() || '',
      'X-Shopify-Timestamp': wishlistShopifySettings?.timestamp || '',
      'X-Shopify-Signature': wishlistShopifySettings?.signature || '',
    },
    credentials: 'include',
  };
};

export const getDeviceID = () => {
  return {
    'x-device-id': getCookie('device_id'),
  };
};

export const getOnlineSalePricing = (tags: string[] | undefined, productPrice?: number, bypassCheck = false) => {
  if (!window?.differential_pricing_settings?.isDifferentialPricingEnabled || !tags) return;

  const onlineSalePriceTags = tags
    .map((tag) => tag.toLowerCase())
    .filter((tag) => tag.startsWith(ONLINE_SALES_PRICE_TAG.toLowerCase()));

  if (onlineSalePriceTags.length > 1) return;

  const onlineSalePriceTag = onlineSalePriceTags[0];
  const [, onlineSalePrice] = onlineSalePriceTag ? onlineSalePriceTag.split(':') : [];
  const onlineSalePriceNumber = Number(onlineSalePrice);

  if (bypassCheck) {
    return onlineSalePriceNumber > 0 ? onlineSalePriceNumber : undefined;
  }
  const passesSanityChecks = productPrice && onlineSalePriceNumber > 0 && onlineSalePriceNumber < productPrice;
  return passesSanityChecks ? onlineSalePriceNumber : undefined;
};

export const checkIfItemCanBeSoldOut = (tags: string[] | null | undefined): boolean => {
  if (!tags) return false;
  return !isTagPresent(tags, DISABLE_SOLD_OUT_MESSAGING_TAG);
};

export const preorderDateRangeCopy = (preorderEstimatedStartDate: string | null | undefined): string | undefined => {
  const preorderBufferDays = window?.preorder?.preorderBufferDays;
  if (!preorderEstimatedStartDate || !preorderBufferDays) return undefined;

  const preorderStartDateCopy = formatDateWithLocaleOptions(preorderEstimatedStartDate);
  const date = new Date(preorderEstimatedStartDate);
  date.setDate(date.getDate() + preorderBufferDays);
  const preorderEndDate = date.toISOString();
  const preorderEndDateCopy = formatDateWithLocaleOptions(preorderEndDate);
  return `${preorderStartDateCopy} — ${preorderEndDateCopy}`;
};

export const capitalizeFirstLetterOfString = (string: string) => {
  const capitalizedFirstLetter = string.charAt(0).toUpperCase();
  return capitalizedFirstLetter + string.slice(1, string.length);
};

export const downcaseObjectKeys = (obj: Record<string, unknown>) => {
  return transform(obj, (result: Record<string, unknown>, val, key) => {
    result[key.toLowerCase()] = val;
  });
};

export const promiseWithTimeout = <T>(ms: number, promise: Promise<T>): Promise<T> => {
  let timeoutId: NodeJS.Timeout;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`Promise timed out after ${ms} ms`));
    }, ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
};

export function jsonToFormData(jsonObject: Record<string, string | Blob>): FormData {
  const formData = new FormData();

  for (const key in jsonObject) {
    const value = jsonObject[key];
    if (jsonObject.hasOwnProperty(key) && value) {
      formData.append(key, value);
    }
  }

  return formData;
}

export function getHrefWithoutOrigin(urlString: string = window.location.href): string {
  const url = new URL(urlString);
  return url.pathname + url.search + url.hash;
}
